// Set up (decision 065): the five steps from the install to the first
// flight together. The ticks follow what the companion sees, the page never
// folds; it leaves the rail when the pilot presses Done, hide this, and
// comes back if a step comes undone. Under the host alone.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { queries } from "@/api";
import { NotOnThisPc } from "@/components/NotOnThisPc";
import { ShareAnAirframe } from "@/components/ShareAirframe";
import { PluginStep } from "@/components/Welcome";
import { External } from "@/components/External";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import type { AppInfo, StatusRow } from "@/host/types";

export const Route = createFileRoute("/setup")({
  component: SetUp,
});

/** What the set-up has reached, from what the shell and the platform say. */
export type SetUpState = {
  signedIn: boolean;
  plugin: boolean | null;
  folder: boolean;
  shared: number;
  flown: number;
  done: number;
  total: number;
  finished: boolean;
};

export function setUpState(info: AppInfo, rows: StatusRow[], signedIn: boolean): SetUpState {
  const shared = rows.filter((r) => r.classification === "shared_by_me" || r.classification === "shared_with_me").length;
  // An aircraft accepted here or a return made here: a followed flight
  // installs too, but is neither; nor is an acceptance without the files.
  const flown = rows.filter((r) => (r.local?.lease && r.on_machine) || r.local?.last_checkin_seq).length;
  const steps = [signedIn, info.plugin_installed === true && info.plugin_current !== false, info.airframes_dir !== null, shared > 0, flown > 0];
  const done = steps.filter(Boolean).length;
  return { signedIn, plugin: info.plugin_installed, folder: info.airframes_dir !== null, shared, flown, done, total: steps.length, finished: done === steps.length };
}

function SetUp() {
  const host = useHost();
  if (!host) return <NotOnThisPc />;
  return <SetUpUnderHost />;
}

function SetUpUnderHost() {
  const host = useHost()!;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const info = useQuery(hostQueries.info(host));
  const status = useQuery(hostQueries.status(host));
  const me = useQuery(queries.me());
  const config = useQuery(queries.config());
  const [code, setCode] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const hide = useMutation({
    mutationFn: () => host.settingsUpdate({ setup_hidden: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["host", "info"] });
      navigate({ to: "/" });
    },
  });
  if (!info.data) return <p className="muted">Reading this PC...</p>;
  const i = info.data;
  const s = setUpState(i, status.data ?? [], !!me.data);
  const openLink = () => {
    const m = code.trim().match(/invites\/([A-Za-z0-9_-]+)/) ?? code.trim().match(/^([A-Za-z0-9_-]{6,})$/);
    if (m) navigate({ to: "/invites/$code", params: { code: m[1] } });
  };
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="page-title">Set up</h1>
          <p className="muted text-sm">Five steps from the install to the first flight together. The ticks follow what ShariFly sees on this PC; the page stays until you hide it.</p>
          <p className="text-sm" data-testid="setup-needs">
            You need X-Plane 12.1.4 or later on Windows with the Challenger 650 installed, and a Discord account. Nothing else: the plugin comes from here, the aircraft come from the Fleet.
          </p>
        </div>
        <span className="flex-1" />
        <span className="pill" data-testid="guide-progress">
          {s.done} of {s.total} done
        </span>
      </header>
      <ol className="steps" aria-label="getting started steps">
        <li className={`step ${s.signedIn ? "step-done" : "step-todo"}`} data-testid="step-login">
          <span className="step-mark" aria-hidden="true">
            {s.signedIn ? "✓" : 1}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="font-semibold">Sign in with Discord</span>
            <span className={`text-xs ${s.signedIn ? "text-ok" : "text-warn"}`}>{s.signedIn ? `Signed in as ${me.data?.user.handle}.` : "Not signed in: the button is at the top."}</span>
          </div>
        </li>
        <PluginStep installed={s.plugin} current={i.plugin_current} ready={i.plugin_ready} />
        <li className={`step ${s.folder ? "step-done" : "step-todo"}`} data-testid="step-folder">
          <span className="step-mark" aria-hidden="true">
            {s.folder ? "✓" : 3}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="font-semibold">Your airframes folder</span>
            <span className="muted text-xs">ShariFly reads X-Plane 12\Output\CL650\airframes, found from your X-Plane installations. It never changes a file of the aircraft: booking an aircraft copies a whole folder in, and the return copies it out.</span>
            <span className={`text-xs ${s.folder ? "text-ok" : "text-warn"}`}>{i.airframes_dir ? `${i.airframes_dir}, ${(status.data ?? []).length} airframes found.` : (i.airframes_dir_error ?? "Not found yet.")}</span>
          </div>
          {s.folder ? null : (
            <Link to="/pc/settings" className="btn btn-sm self-center">
              Set the folder
            </Link>
          )}
        </li>
        <li className={`step ${s.shared > 0 ? "step-done" : "step-todo"}`} data-testid="step-share">
          <span className="step-mark" aria-hidden="true">
            {s.shared > 0 ? "✓" : 4}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="font-semibold">Share an airframe, or join a friend's</span>
            <span className="muted text-xs">Sharing puts one of your airframes on ShariFly and gives you a link for your friends. A friend's link makes you a pilot of theirs.</span>
            <span className={`text-xs ${s.shared > 0 ? "text-ok" : "muted"}`}>
              {s.shared > 0 ? `${(status.data ?? []).filter((r) => r.classification === "shared_by_me").length} shared by you, ${(status.data ?? []).filter((r) => r.classification === "shared_with_me").length} shared with you.` : (status.data ?? []).length > 0 ? `${(status.data ?? []).length} airframes on this PC, none shared yet.` : "No airframe found yet."}
            </span>
            <div className="flex flex-wrap items-start gap-2">
              <ShareAnAirframe />
              <button type="button" className="btn" onClick={() => setLinkOpen((o) => !o)} data-testid="have-a-link">
                I have a link
              </button>
            </div>
            {linkOpen ? (
              <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  openLink();
                }}
              >
                <input className="input flex-1" aria-label="The invite link" placeholder="paste the link your friend sent" value={code} onChange={(e) => setCode(e.target.value)} />
                <button type="submit" className="btn btn-primary" disabled={!code.trim()} data-testid="open-link">
                  Open it
                </button>
              </form>
            ) : null}
          </div>
        </li>
        <li className={`step ${s.flown > 0 ? "step-done" : "step-todo"}`} data-testid="step-fly">
          <span className="step-mark" aria-hidden="true">
            {s.flown > 0 ? "✓" : 5}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="font-semibold">Book an aircraft and fly once</span>
            <span className="muted text-xs">On the Fleet, press Book the aircraft with X-Plane closed, then start X-Plane and fly. ShariFly uploads the aircraft when you close X-Plane parked and shut down. Only the pilot who has it can fly it meanwhile.</span>
            <span className={`text-xs ${s.flown > 0 ? "text-ok" : "muted"}`}>{s.flown > 0 ? `${s.flown} aircraft booked or returned from this PC.` : "Ticks after the first booking or return made from this PC."}</span>
          </div>
          {s.flown > 0 ? null : (
            <Link to="/" className="btn btn-sm self-center">
              Open the Fleet
            </Link>
          )}
        </li>
      </ol>
      {s.finished ? (
        <section className="notice flex flex-wrap items-center gap-3" data-testid="setup-finished">
          <span className="flex-1">You are flying. ShariFly uploads the aircraft when you close X-Plane; the Fleet tells you who has what. This page can go; it comes back by itself if a step comes undone, and the same words live under Help.</span>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => hide.mutate()} disabled={hide.isPending} data-testid="setup-hide">
            Done, hide this
          </button>
        </section>
      ) : (
        <p className="muted text-xs">Nothing in your aircraft's files is ever modified: ShariFly copies whole folders, only while X-Plane is closed, and keeps a backup of every booking under its home.</p>
      )}
      {config.data?.discord_invite ? (
        <p className="muted text-xs" data-testid="setup-crew-room">
          Not a step, but worth it: <External href={config.data.discord_invite}>join the crew room on Discord</External>, where bugs and the word on new versions go. The badge, and the Verified role on Discord, follow at your next sign-in.
        </p>
      ) : null}
    </div>
  );
}
